// ABOUTME: Visual goal progress indicator with hearts, stars, and squares
// ABOUTME: Shows filled/outlined icons to represent progress toward goal completion

import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Tooltip, LinearProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Repeat as RepeatIcon, Shield as ShieldIcon } from '@mui/icons-material';
import { GoalProgressIndicator as GoalProgressData, GoalType } from '../types/api';
import { getFullApiUrl } from '../utils/routeUtils';
import { isGoalEndingToday, formatShortDate } from '../utils/dateUtils';
import naluV1Theme from '../theme/theme.nalu-v1';

interface GoalProgressIndicatorProps {
  goalProgress: GoalProgressData;
  isCelebrating?: boolean;
  onFadeComplete?: () => void;
  familyTimezone?: string;
}

const GoalProgressIndicator: React.FC<GoalProgressIndicatorProps> = ({
  goalProgress,
  isCelebrating = false,
  onFadeComplete,
  familyTimezone
}) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // State for tracking fade-out phase (after 15s celebration)
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Handle celebration timing: 15 seconds of glow, then fade-out
  useEffect(() => {
    if (!isCelebrating) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- synchronous reset when not celebrating; no async path, derived state would require restructuring the full animation state machine
      setIsFadingOut(false);
      return;
    }

    // Skip animations for reduced motion - call onFadeComplete immediately
    if (prefersReducedMotion) {
      // For reduced motion: show static green for 15s, then remove instantly
      const removeTimer = setTimeout(() => {
        onFadeComplete?.();
      }, 15000);
      return () => clearTimeout(removeTimer);
    }

    // After 15 seconds of celebrating, start fade-out
    const fadeTimer = setTimeout(() => {
      setIsFadingOut(true);
    }, 15000);

    return () => clearTimeout(fadeTimer);
  }, [isCelebrating, prefersReducedMotion, onFadeComplete]);

  // After fade-out starts, call onFadeComplete once the 1s CSS transition completes
  useEffect(() => {
    if (!isFadingOut) return;
    const completionTimer = setTimeout(() => {
      onFadeComplete?.();
    }, 1000); // Match the 1s opacity/transform CSS transition duration
    return () => clearTimeout(completionTimer);
  }, [isFadingOut, onFadeComplete]);

  // Calculate progress percentage
  const progressPercentage = goalProgress.targetValue > 0
    ? Math.round((goalProgress.currentValue / goalProgress.targetValue) * 100)
    : 0;

  // Progress bar color - consistent blue for all goal types (Issue #652)
  const progressBarColor = theme.palette.info.main;

  // Format overlaid progress text as "current/target (percentage%)"
  const progressText = `${goalProgress.currentValue}/${goalProgress.targetValue} (${progressPercentage}%)`;

  // Issue #1309: Calculate days remaining display
  // Only show for goals with future EndDates (not past dates from seed data)
  const getDaysRemainingText = (): string | null => {
    if (goalProgress.daysRemaining === null || goalProgress.daysRemaining === undefined) {
      return null;
    }
    // Check if EndDate is in the past (daysRemaining=0 but EndDate < today)
    if (goalProgress.endDate && !isGoalEndingToday(goalProgress.endDate, familyTimezone)) {
      return null; // Don't show "Ends today" for already-ended goals
    }
    if (goalProgress.daysRemaining === 0) {
      return 'Ends today';
    }
    if (goalProgress.daysRemaining === 1) {
      return '1 day left';
    }
    return `${goalProgress.daysRemaining} days left`;
  };

  // Issue #1309: Get urgency color for days remaining
  const getDaysRemainingColor = (): string => {
    if (goalProgress.daysRemaining === null || goalProgress.daysRemaining === undefined) {
      return theme.palette.success.main;
    }
    if (goalProgress.daysRemaining < 3) {
      return theme.palette.error.main;
    }
    if (goalProgress.daysRemaining <= 7) {
      return theme.palette.warning.main;
    }
    return theme.palette.success.main;
  };

  // Issue #1309: Calculate day passes display (HeartStreak and HeartCollection)
  const getDayPassesText = (): string | null => {
    // Show for HeartStreak and HeartCollection goals with dayPasses > 0
    if ((goalProgress.goalType !== GoalType.HeartStreak && goalProgress.goalType !== GoalType.HeartCollection) || goalProgress.dayPasses <= 0) {
      return null;
    }
    const remaining = goalProgress.dayPasses - goalProgress.dayPassesUsed;
    const passWord = remaining === 1 ? 'day pass' : 'day passes';
    return `${remaining}/${goalProgress.dayPasses} ${passWord} left`;
  };

  // Issue #1421: Format lifecycle display (replaces Issue #1415 getStartDateText)
  const getLifecycleText = (): string => {
    const formatDate = (dateStr: string | undefined): string | null => {
      if (!dateStr) return null;

      const datePart = dateStr.split('T')[0];
      const parts = datePart.split('-').map(Number);

      if (parts.length < 3 || parts.some(isNaN)) return null;

      const [year, month, day] = parts;
      const today = new Date();
      const sameYear = year === today.getFullYear();

      const monthDate = new Date(year, month - 1, day);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });

      if (sameYear) {
        return `${monthName} ${day}`;
      }
      return `${monthName} ${day}, ${year}`;
    };

    const parts: string[] = [];

    // Start date (or placeholder)
    const startFormatted = formatDate(goalProgress.startDate);
    parts.push(`Started ${startFormatted || '?'}`);

    // Completed or Failed
    if (goalProgress.completedAt) {
      const completedFormatted = formatDate(goalProgress.completedAt);
      if (completedFormatted) {
        parts.push(`Completed ${completedFormatted}`);
      }
    } else if (goalProgress.failedAt) {
      const failedFormatted = formatDate(goalProgress.failedAt);
      if (failedFormatted) {
        parts.push(`Failed ${failedFormatted}`);
      }
    }

    // Celebrated (only if present)
    if (goalProgress.celebratedAt) {
      const celebratedFormatted = formatDate(goalProgress.celebratedAt);
      if (celebratedFormatted) {
        parts.push(`Celebrated ${celebratedFormatted}`);
      }
    }

    return parts.join(' → ');
  };

  const daysRemainingText = getDaysRemainingText();
  const dayPassesText = getDayPassesText();
  // Row 3 only shows if there's days remaining OR day passes (startDate moved to Row 4)
  const showRow3 = daysRemainingText !== null || dayPassesText !== null;

  // Issue #1415: Celebration styles
  const celebrationStyles = isCelebrating ? {
    bgcolor: 'success.light',
    borderColor: 'success.main',
    // Pulsing glow animation (green) - respects reduced motion
    ...(!prefersReducedMotion && !isFadingOut && {
      animation: 'goalPulse 1s ease-in-out infinite',
      boxShadow: `0 0 20px 5px ${alpha(theme.palette.success.main, 0.6)}`,
      '@keyframes goalPulse': {
        '0%, 100%': { boxShadow: `0 0 20px 5px ${alpha(theme.palette.success.main, 0.6)}` },
        '50%': { boxShadow: `0 0 30px 10px ${alpha(theme.palette.success.main, 0.8)}` },
      },
    }),
    // Fade-out animation (slide-up + fade)
    ...(isFadingOut && {
      opacity: 0,
      transform: 'translateY(-20px)',
      transition: 'opacity 1s ease-out, transform 1s ease-out',
    }),
  } : {};

  return (
    <Box
      data-testid={isCelebrating ? 'celebrating-goal' : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        my: 0.5,
        width: '100%',
        minHeight: 'auto',
        p: 1.5,
        // Solid panel fill, no border/shadow — matches the mockup's goal mini-card (issue #1900)
        ...(isNaluV1Theme
          ? { bgcolor: 'background.paper', borderRadius: '14px', boxShadow: 'none' }
          : { border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }
        ),
        transition: isCelebrating && !isFadingOut ? 'none' : 'opacity 1s ease-out, transform 1s ease-out',
        ...celebrationStyles,
      }}
    >
      {/* Row 1: Goal Title and Reward */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}>
        {/* Reward image — left-aligned circle */}
        {goalProgress.rewardImageUrl && (
          <Box
            component="img"
            src={getFullApiUrl(goalProgress.rewardImageUrl)}
            alt={`Reward image for ${goalProgress.goalTitle}`}
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              objectFit: 'cover',
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'primary.light',
            }}
          />
        )}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          minWidth: 0,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.85rem',
              lineHeight: 1.3,
              wordBreak: 'break-word',
              flex: 1
            }}
          >
            {goalProgress.goalTitle}
          </Typography>
          {goalProgress.isRecurring && (
            <Tooltip title="Recurring goal">
              <Chip
                data-testid="repeat-icon"
                icon={isNaluV1Theme ? undefined : <RepeatIcon sx={{ fontSize: 12 }} />}
                label="Recurring"
                size="small"
                sx={isNaluV1Theme ? {
                  // Solid teal pill, plain text, no icon — matches the mockup's goal badge
                  backgroundColor: 'info.main',
                  color: 'primary.dark',
                  fontWeight: 700,
                  height: 18,
                  fontSize: '0.6rem',
                  '& .MuiChip-label': { px: 1 },
                } : {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  height: 18,
                  fontSize: '0.6rem',
                  '& .MuiChip-icon': {
                    fontSize: 12,
                    color: theme.palette.info.main
                  },
                  '& .MuiChip-label': {
                    px: 0.5
                  }
                }}
              />
            </Tooltip>
          )}
          {/* Nuclear reset exemption indicator (Issue #1310) */}
          {goalProgress.isNukeExempt && (
            <Tooltip title="Protected from nuclear reset">
              {/* Icon glyph sizing via fontSize, not a Typography text override */}
              <ShieldIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5 }} />
            </Tooltip>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: isNaluV1Theme ? 400 : 600,
            color: isNaluV1Theme ? 'text.secondary' : 'secondary.main',
            fontSize: '0.75rem',
            fontStyle: 'italic',
            lineHeight: 1.2
          }}
        >
          {goalProgress.rewardText}
        </Typography>
        </Box>
      </Box>

      {/* Row 2: Progress Bar - Full Width */}
      {isNaluV1Theme ? (
        <Box sx={{ width: '100%', mt: 1 }}>
          {/* Thin bar, no overlaid text — matches the mockup's goal progress bar */}
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            role="progressbar"
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Goal progress: ${goalProgress.currentValue} of ${goalProgress.targetValue} complete (${progressPercentage}%)`}
            sx={{
              height: '12px',
              borderRadius: '6px',
              bgcolor: alpha(theme.palette.warning.main, 0.25),
              '& .MuiLinearProgress-bar': { borderRadius: '6px', bgcolor: 'primary.light' },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{progressText}</Typography>
            {goalProgress.startDate && (
              <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                Started {formatShortDate(goalProgress.startDate)}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{
          width: '100%',
          position: 'relative',
          mt: 1
        }}>
          {/* Linear Progress Bar */}
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            role="progressbar"
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Goal progress: ${goalProgress.currentValue} of ${goalProgress.targetValue} complete (${progressPercentage}%)`}
            sx={{
              height: 24,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.common.black, 0.1),
              '& .MuiLinearProgress-bar': {
                backgroundColor: progressBarColor,
                borderRadius: 2
              }
            }}
          />

          {/* Overlaid Progress Text */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'white',
              textShadow: `1px 1px 2px ${alpha(theme.palette.common.black, 0.7)}`,
              pointerEvents: 'none'
            }}
          >
            {progressText}
          </Box>
        </Box>
      )}

      {/* Row 3: Days Remaining and Day Passes (Issue #1309) */}
      {showRow3 && (
        <Box
          data-testid="goal-details-row"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 0.5
          }}
        >
          {/* Left side: Days Remaining */}
          {daysRemainingText ? (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                color: getDaysRemainingColor()
              }}
            >
              {daysRemainingText}
            </Typography>
          ) : (
            <Box />
          )}

          {/* Right side: Day Passes */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Day Passes */}
            {dayPassesText && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  color: 'text.secondary'
                }}
              >
                {dayPassesText}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Row 4: Lifecycle Display (Issue #1421) */}
      <Box
        data-testid="goal-lifecycle-row"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 0.5
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            color: 'text.secondary'
          }}
        >
          {getLifecycleText()}
        </Typography>
      </Box>
    </Box>
  );
};

export default GoalProgressIndicator;
